import { CheckCircle2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Chapter, Reveal, MarqueeStrip } from "@/components/motion";
import { IMAGES, MARQUEE_ITEMS } from "@/data/site";

const EXPERIENCE = [
  "Workforce management",
  "Construction operations",
  "Safety and compliance",
  "Financial management",
  "Payroll and workforce administration",
  "Client relationship management",
  "Workforce welfare and support",
  "Logistics and workforce coordination",
];

const COMMITMENT = ["Right Person.", "Right Role.", "Right Support.", "Right Standard."];

const RELIABILITY = [
  "Responsive communication",
  "Professional workforce coordination",
  "Reliable personnel",
  "Flexible workforce solutions",
  "Experienced management",
  "Ongoing client support",
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        index="01"
        label="About Formcore"
        titleLines={["Experience. Capability.", { t: "Accountability.", c: "text-gold" }]}
        intro="Formcore Constructions was established to provide professional labour hire services to the construction industry — built on more than two decades of construction and workforce management experience."
        image={IMAGES.plans}
      />

      <section className="bg-white py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Chapter index="02" label="Our Story" />
              <Reveal delay={0.1}>
                <h2 className="mt-8 font-heading text-3xl font-extrabold leading-[1.1] tracking-tight text-navy sm:text-4xl">
                  Established in NSW. <span className="text-gold">Expanding Nationally.</span>
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-6 text-base leading-relaxed text-charcoal/75">
                  The business was initially developed in New South Wales and has achieved significant growth,
                  establishing a workforce of more than 150 personnel across NSW. Building on this success, Formcore
                  is expanding its operations into Queensland, Victoria and the ACT, with a broader national strategy
                  focused on connecting experienced construction personnel with reputable construction businesses
                  across Australia.
                </p>
                <p className="mt-5 text-base leading-relaxed text-charcoal/75">
                  We understand that successful labour hire is not simply about supplying people. It is about providing
                  the right people, appropriate support, effective communication and professional management throughout
                  the engagement.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={0.15} className="relative">
                <div className="absolute -bottom-5 -right-5 hidden h-full w-full border-2 border-gold md:block" />
                <img
                  src={IMAGES.intro}
                  alt="Formcore management coordinating workforce on site"
                  className="clip-frame relative h-[320px] w-full object-cover md:h-[440px]"
                />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy py-28 lg:py-32">
        <div className="absolute inset-0 bg-blueprint-dark opacity-30" />
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <Chapter index="03" label="Management Experience" dark />
          <Reveal delay={0.1}>
            <h2 className="mt-8 max-w-3xl font-heading text-3xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-4xl">
              More Than 20 Years Behind <span className="text-gold">Every Placement</span>
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70">
              Behind Formcore is a management team with more than 20 years of experience across the construction
              industry and labour hire sector — including working with and managing workforces supporting Tier 1
              and major construction companies.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {EXPERIENCE.map((item, i) => (
              <Reveal key={item} delay={(i % 4) * 0.06} className="h-full">
                <div className="flex h-full items-start gap-3 bg-navy p-6">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                  <span className="text-sm font-semibold text-white/85">{item}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-2xl text-sm leading-relaxed text-white/55">
              This experience provides Formcore with an understanding of the pressures faced by both construction
              businesses and workers.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-surface py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Chapter index="04" label="Our Commitment" />
          <Reveal delay={0.1}>
            <h2 className="mt-8 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Quality in <span className="text-gold">Every Placement</span>
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-charcoal/75">
              Our personnel represent Formcore while working within our clients' businesses and project environments.
              That responsibility is taken seriously — from initial workforce selection through to ongoing management.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {COMMITMENT.map((item, i) => (
              <Reveal key={item} delay={i * 0.08}>
                <div className="border-l-4 border-gold bg-white p-7">
                  <span className="font-heading text-xs font-bold text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-3 font-heading text-xl font-extrabold tracking-tight text-navy">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-20 grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Chapter index="05" label="Reliability" />
              <Reveal delay={0.1}>
                <h3 className="mt-8 font-heading text-3xl font-extrabold tracking-tight text-navy">
                  When You Need People, <span className="text-gold">We Deliver</span>
                </h3>
                <p className="mt-6 text-base leading-relaxed text-charcoal/75">
                  Construction schedules do not always wait. Formcore is structured to provide responsive workforce
                  solutions that adapt to changing project requirements, with an experienced management team providing
                  a direct point of contact for every client.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <div className="grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2">
                {RELIABILITY.map((item, i) => (
                  <Reveal key={item} delay={i * 0.05} className="h-full">
                    <div className="flex h-full items-center gap-3 bg-white p-5">
                      <span className="block h-2 w-2 rotate-45 bg-gold" />
                      <span className="text-sm font-semibold text-navy">{item}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <MarqueeStrip items={MARQUEE_ITEMS} dark />
      <CTASection title="Looking for a Workforce Partner You Can Rely On?" />
    </div>
  );
}
